import { ColumnDef } from "@tanstack/react-table";
import {
  MoreHorizontal,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Key,
  Trash2,
} from "lucide-react";
import {
  Button,
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Checkbox,
} from "@b2b/ui-components";
import { SellerStatus } from "@b2b/services";
import { PendingUser, userRowShowsApproveReject } from "../../types/user.types";
import { BaseColumnDef } from "@b2b/ui-components/components/Table/types";

interface CreateUserColumnsOptions {
  loading?: boolean;
  actionsLoading?: string | boolean;
  selectedUsers?: Set<string>;
  allUsers?: PendingUser[];
  onToggleSelection?: (userId: string) => void;
  onToggleSelectAll?: () => void;
  onViewProfile: (userId: string) => void;
  onEditUser: (user: PendingUser) => void;
  onApproveUser?: (userId: string, user: PendingUser) => void;
  onRejectUser?: (userId: string, user: PendingUser) => void;
  onActivateUser?: (userId: string) => void;
  onSuspendUser?: (userId: string) => void;
  onResetPassword?: (userId: string) => void;
  onDeleteUser: (userId: string) => void;
  showSelectColumn?: boolean;
}

export function createUserColumns(
  options: CreateUserColumnsOptions
): ColumnDef<PendingUser>[] {
  const {
    loading = false,
    actionsLoading = false,
    selectedUsers = new Set(),
    allUsers = [],
    onToggleSelection,
    onToggleSelectAll,
    onViewProfile,
    onEditUser,
    onApproveUser,
    onRejectUser,
    onActivateUser,
    onSuspendUser,
    onResetPassword,
    onDeleteUser,
    showSelectColumn = false,
  } = options;

  const columns: ColumnDef<PendingUser>[] = [];

  // Add select column if needed (for bulk actions)
  if (showSelectColumn && onToggleSelection) {
    const selectedCurrentPageCount = allUsers.reduce(
      (count, user) => (selectedUsers.has(user.id) ? count + 1 : count),
      0
    );
    const isAllSelected =
      allUsers.length > 0 && selectedCurrentPageCount === allUsers.length;
    const isIndeterminate =
      selectedCurrentPageCount > 0 && selectedCurrentPageCount < allUsers.length;

    columns.push({
      id: "select",
      header: () => {
        if (!onToggleSelectAll) return null;
        return (
          <Checkbox
            checked={isAllSelected}
            onCheckedChange={onToggleSelectAll}
            ref={(el) => {
              if (el) {
                (el as any).indeterminate = isIndeterminate;
              }
            }}
          />
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <Checkbox
            checked={selectedUsers.has(user.id)}
            onCheckedChange={() => onToggleSelection(user.id)}
          />
        );
      },
      enableSorting: false,
      align: "left",
      size: 50,
    } as BaseColumnDef<PendingUser>);
  }

  // User column
  columns.push({
    accessorKey: "firstName",
    header: "User",
    size: 200,
    cell: ({ row }) => {
      const user = row.original;
      const getUserName = () => {
        if (user.firstName && user.lastName) {
          return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
      };

      const getUserInitials = () => {
        if (user.firstName && user.lastName) {
          return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
        }
        return user.email[0].toUpperCase();
      };

      const userName = getUserName();
      const profileImageUrl =
        user.profileImage ||
        (user as PendingUser & { avatarUrl?: string; imageUrl?: string })
          .avatarUrl ||
        (user as PendingUser & { avatarUrl?: string; imageUrl?: string })
          .imageUrl ||
        (user.role === "SELLER" || user.role === "BUYER_SELLER"
          ? (user.company as { logo?: string } | undefined)?.logo
          : undefined);

      return (
        <div className="flex items-center space-x-3 min-w-0 max-w-[200px]">
          <Avatar className="h-8 w-8 flex-shrink-0">
            {profileImageUrl ? (
              <AvatarImage src={profileImageUrl} alt={userName} />
            ) : null}
            <AvatarFallback className="bg-orange-100 text-orange-500">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate" title={userName}>
              {userName}
            </div>
          </div>
        </div>
      );
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Email column
  columns.push({
    accessorKey: "email",
    header: "Email",
    size: 200,
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className="text-sm text-gray-900 truncate max-w-[200px]" title={user.email}>
          {user.email}
        </div>
      );
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Type/Role column
  columns.push({
    accessorKey: "role",
    header: "Type",
    size: 120,
    cell: ({ row }) => {
      const user = row.original;
      const roleText = user.role.toLowerCase().replace("_", " ");
      return (
        <Badge variant="outline" className="capitalize truncate max-w-[120px]" title={roleText}>
          {roleText}
        </Badge>
      );
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Status column
  columns.push({
    accessorKey: "isActive",
    header: "Status",
    size: 120,
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className="flex flex-col gap-1">
          {user.role === "SELLER" || user.role === "BUYER_SELLER" ? (
            <Badge
              variant={
                user.sellerStatus === SellerStatus.APPROVED
                  ? "default"
                  : user.sellerStatus === SellerStatus.REJECTED
                    ? "destructive"
                    : user.sellerStatus === SellerStatus.SUSPENDED
                      ? "secondary"
                      : "outline"
              }
            >
              {user.sellerStatus === SellerStatus.APPROVED
                ? "Approved"
                : user.sellerStatus === SellerStatus.REJECTED
                  ? "Rejected"
                  : user.sellerStatus === SellerStatus.SUSPENDED
                    ? "Suspended"
                    : "Pending Approval"}
            </Badge>
          ) : (
            <Badge
              variant={
                user.isActive
                  ? "default"
                  : user.company?.approvalStatus === "rejected"
                    ? "destructive"
                    : "secondary"
              }
            >
              {user.isActive
                ? "Approved"
                : user.company?.approvalStatus === "rejected"
                  ? "Rejected"
                  : "Pending"}
            </Badge>
          )}
        </div>
      );
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Join Date column
  columns.push({
    accessorKey: "createdAt",
    header: "Join Date",
    size: 100,
    cell: ({ row }) => {
      const user = row.original;
      return <span>{new Date(user.createdAt).toLocaleDateString()}</span>;
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Company column
  columns.push({
    accessorKey: "company",
    header: "Company",
    size: 150,
    cell: ({ row }) => {
      const user = row.original;
      const companyName =
        user.role === "BUYER" ? "" : (user.company?.name ?? "");
      return (
        <span className="truncate block max-w-[150px]" title={companyName}>
          {companyName}
        </span>
      );
    },
    enableSorting: true,
    align: "left",
  } as BaseColumnDef<PendingUser>);

  // Actions column
  columns.push({
    id: "actions",
    header: "Actions",
    size: 80,
    cell: ({ row }) => {
      const user = row.original;
      const isLoading = actionsLoading === user.id || actionsLoading === true;

      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" disabled={isLoading}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={() => onViewProfile(user.id)}>
                <Eye className="mr-2 h-4 w-4" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEditUser(user)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit User
              </DropdownMenuItem>
              {!user.isActive && onActivateUser && (
                <DropdownMenuItem
                  onClick={() => onActivateUser(user.id)}
                  disabled={isLoading}
                >
                  <UserCheck className="mr-2 h-4 w-4 text-green-600" />
                  Activate
                </DropdownMenuItem>
              )}
              {user.isActive && onSuspendUser && (
                <DropdownMenuItem
                  onClick={() => onSuspendUser(user.id)}
                  disabled={isLoading}
                >
                  <UserX className="mr-2 h-4 w-4 text-orange-600" />
                  Suspend
                </DropdownMenuItem>
              )}
              {userRowShowsApproveReject(user) &&
                onApproveUser &&
                onRejectUser && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onApproveUser(user.id, user)}
                      disabled={isLoading}
                    >
                      <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onRejectUser(user.id, user)}
                      disabled={isLoading}
                      className="text-red-600"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Reject
                    </DropdownMenuItem>
                  </>
                )}
              {onResetPassword && (
                <DropdownMenuItem
                  onClick={() => onResetPassword(user.id)}
                  disabled={isLoading}
                >
                  <Key className="mr-2 h-4 w-4" />
                  Reset Password
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDeleteUser(user.id)}
                disabled={isLoading}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
    enableSorting: false,
    align: "right",
  } as BaseColumnDef<PendingUser>);

  return columns;
}
